import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { toast } from 'react-toastify';
import './PublicMarket.css';
import ImageGallery from '../components/ImageGallery';

const PublicMarket = () => {
  const { username } = useParams();
  const { t, i18n } = useTranslation();
  const [market, setMarket] = useState(null);
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [searchTerm, setSearchTerm] = useState('');
  const [itemQuantities, setItemQuantities] = useState({});

  useEffect(() => {
    // Load market data first to get the default language
    loadMarketData();
  }, [username]);

  const loadMarketData = async () => {
    try {
      const marketRes = await api.get(
        `/auth/market/${encodeURIComponent(username)}/`
      );
      setMarket(marketRes.data);
      const ownerLang = marketRes.data.owner_language || 'en';
      setDefaultLanguage(ownerLang);
      setCurrentLanguage(ownerLang);
      
      // Now fetch items with the correct language
      await fetchMarketItems(marketRes.data.id, ownerLang);
    } catch (error) {
      console.error('Error fetching market data:', error);
      toast.error(t('marketNotFound') || 'Market not found');
      setLoading(false);
    }
  };

  const fetchMarketItems = async (marketId, lang) => {
    try {
      const itemsRes = await api.get(
        `/auth/market/${marketId}/items/?lang=${lang}`
      );
      
      // Validate items and ensure required fields
      const validItems = itemsRes.data.items.map(item => {
        // Ensure we have required fields
        if (!item.name) {
          return null;
        }
        if (!item.currency) {
          item.currency = 'USD';
        }
        if (typeof item.selling_price !== 'number') {
          return null;
        }
        return item;
      }).filter(item => item !== null);
      
      setItems(validItems);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching market items:', error);
      setLoading(false);
    }
  };

  const handleLanguageChange = async (lang) => {
    setCurrentLanguage(lang);
    await i18n.changeLanguage(lang);
    
    // Clear cart when language changes
    setCart([]);
    setShowCart(false);
    
    // Re-fetch items with new language
    if (market) {
      setLoading(true);
      await fetchMarketItems(market.id, lang);
    }
  };

  const addToCart = (item, customQuantity = null) => {
    const requestedQuantity = customQuantity || itemQuantities[item.id] || 1;
    const qtyToAdd = item.allow_fractional 
      ? parseFloat(parseFloat(requestedQuantity).toFixed(3))
      : Math.round(requestedQuantity);
    
    if (qtyToAdd <= 0) {
      toast.error(t('invalidQuantity') || 'Invalid quantity');
      return;
    }
    
    const existingItem = cart.find(ci => ci.id === item.id);
    
    if (existingItem) {
      const newQuantity = existingItem.quantity + qtyToAdd;
      
      if (newQuantity <= item.quantity) {
        setCart(cart.map(ci =>
          ci.id === item.id
            ? { ...ci, quantity: parseFloat(newQuantity.toFixed(3)) }
            : ci
        ));
        toast.success(t('addedToCart') || 'Added to cart');
      } else {
        toast.error(t('insufficientStock') || 'Insufficient stock');
      }
    } else {
      if (qtyToAdd <= item.quantity) {
        setCart([...cart, { ...item, quantity: qtyToAdd }]);
        toast.success(t('addedToCart') || 'Added to cart');
      } else {
        toast.error(t('insufficientStock') || 'Insufficient stock');
      }
    }
    
    // Reset quantity input for this item
    setItemQuantities(prev => ({ ...prev, [item.id]: '' }));
  };
  
  const updateItemQuantity = (itemId, value) => {
    setItemQuantities(prev => ({ ...prev, [itemId]: value }));
  };
  
  const getItemQuantityValue = (itemId, allowFractional) => {
    const value = itemQuantities[itemId];
    if (value === '' || value === undefined) {
      return allowFractional ? '1' : '1';
    }
    return value;
  };

  const formatQuantity = (qty) => {
    const num = parseFloat(qty);
    // Round to max 3 decimal places and remove trailing zeros
    return parseFloat(num.toFixed(3)).toString();
  };

  const getUnitLabel = (unitType) => {
    const labels = {
      'quantity': t('pieces') || 'pieces',
      'weight': t('kg') || 'kg',
      'length': t('meter') || 'm',
      'area': t('squareMeter') || 'm²',
      'volume': t('liter') || 'L'
    };
    return labels[unitType] || '';
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
    } else {
      const item = items.find(i => i.id === itemId);
      if (quantity <= item.quantity) {
        // Round to 3 decimal places for fractional items
        const roundedQuantity = item.allow_fractional 
          ? parseFloat(quantity.toFixed(3))
          : Math.round(quantity);
        
        setCart(cart.map(ci =>
          ci.id === itemId
            ? { ...ci, quantity: roundedQuantity }
            : ci
        ));
      } else {
        toast.error(t('insufficientStock') || 'Insufficient stock');
      }
    }
  };

  const calculateTotals = () => {
    // Separate totals by currency with high precision for large numbers (LBP)
    let totalUSD = 0;
    let totalLBP = 0;

    cart.forEach(item => {
      const price = parseFloat(item.selling_price);
      const qty = parseFloat(item.quantity);
      const subtotal = price * qty;
      
      if (item.currency === 'USD') {
        totalUSD += subtotal;
      } else if (item.currency === 'LBP') {
        totalLBP += subtotal;
      }
    });

    // Get exchange rate from market data
    const exchangeRate = market?.exchange_rate || 89500;
    
    // Calculate grand totals in both currencies
    const grandTotalUSD = totalUSD + (totalLBP / exchangeRate);
    const grandTotalLBP = totalLBP + (totalUSD * exchangeRate);

    return {
      totalUSD: formatCurrency(totalUSD, 'USD'),
      totalLBP: formatCurrency(totalLBP, 'LBP'),
      totalUSDRaw: totalUSD,
      totalLBPRaw: totalLBP,
      grandTotalUSD: formatCurrency(grandTotalUSD, 'USD'),
      grandTotalLBP: formatCurrency(grandTotalLBP, 'LBP'),
      grandTotalUSDRaw: grandTotalUSD,
      grandTotalLBPRaw: grandTotalLBP,
      hasUSD: totalUSD > 0,
      hasLBP: totalLBP > 0,
      hasBothCurrencies: totalUSD > 0 && totalLBP > 0,
      exchangeRate: exchangeRate
    };
  };

  const formatCurrency = (amount, currency) => {
    if (currency === 'LBP') {
      // For LBP, round to nearest whole number (no decimals)
      return Math.round(amount).toLocaleString();
    } else {
      // For USD, use 2 decimal places
      return amount.toFixed(2);
    }
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredItems = items.filter(item => {
    if (!normalizedSearchTerm) {
      return true;
    }

    const searchableText = [item.name, item.category, item.brand]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(normalizedSearchTerm);
  });

  const availableItems = filteredItems.filter(item => item.is_available);
  const unavailableItems = filteredItems.filter(item => !item.is_available);

  const sendWhatsAppOrder = () => {
    if (cart.length === 0) {
      toast.error(t('cartEmpty') || 'Cart is empty');
      return;
    }

    // Validate market phone
    if (!market || !market.phone || market.phone.trim() === '') {
      toast.error(t('phoneNumberNotAvailable') || 'Phone number not available');
      return;
    }

    // Calculate totals by currency
    const totals = calculateTotals();

    // Build WhatsApp message
    let message = `*${t('newOrder')}*\n\n`;
    message += `*${t('market')}:* ${market.name}\n\n`;
    message += `*${t('items')}:*\n`;

    cart.forEach(item => {
      message += `• ${item.name}\n`;
      message += `  ${t('qty')}: ${item.quantity}\n`;
      message += `  ${t('price')}: ${formatCurrency(item.selling_price, item.currency)} ${item.currency}\n`;
      const subtotal = parseFloat(item.selling_price) * parseFloat(item.quantity);
      message += `  ${t('subtotal')}: ${formatCurrency(subtotal, item.currency)} ${item.currency}\n\n`;
    });

    message += `*${t('total')}:*\n`;
    if (totals.hasUSD) {
      message += `  USD: ${totals.totalUSD}\n`;
    }
    if (totals.hasLBP) {
      message += `  LBP: ${totals.totalLBP}\n`;
    }
    
    if (totals.hasBothCurrencies) {
      message += `\n*${t('grandTotal')}:*\n`;
      message += `  ${totals.grandTotalUSD} USD\n`;
      message += `  ${totals.grandTotalLBP} LBP\n`;
      message += `  ${t('exchangeRate')}: 1 USD = ${formatCurrency(totals.exchangeRate, 'LBP')} LBP\n`;
    } else {
      message += `\n*${t('grandTotal')}:*\n`;
      message += `  ${totals.grandTotalUSD} USD\n`;
      message += `  ${totals.grandTotalLBP} LBP\n`;
    }
    
    message += `\n*${t('customer')}:* `;

    // Extract only digits from phone number
    const phoneNumber = market.phone.replace(/[^0-9]/g, '');
    
    if (!phoneNumber) {
      toast.error(t('invalidPhoneNumber') || 'Invalid phone number');
      return;
    }

    // Encode message for WhatsApp
    const waLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(waLink, '_blank');
    toast.success(t('whatsAppOpenedSuccess') || 'WhatsApp will open shortly');
  };

  if (loading) {
    return (
      <div className="public-market-page">
        <div className="loading">{t('loading') || 'Loading...'}</div>
      </div>
    );
  }

  if (!market) {
    return (
      <div className="public-market-page">
        <div className="error">{t('marketNotFound') || 'Market not found'}</div>
      </div>
    );
  }

  return (
    <div className="public-market-page" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header with Market Info */}
      <div className="market-header">
        <div className="market-info">
          <h1>{market.name}</h1>
          <p>{market.phone}</p>
        </div>
        
        <div className="header-actions">
          <select 
            className="language-selector"
            value={currentLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
            <option value="fr">Français</option>
          </select>
          
          <button
            className={`cart-button ${cart.length > 0 ? 'has-items' : ''}`}
            onClick={() => setShowCart(!showCart)}
          >
            🛒 {t('cart')} ({cart.length})
          </button>
        </div>
      </div>

      <div className="market-content">
        {/* Items Grid */}
        {!showCart && (
          <div className="items-container">
            <h2>{t('items')} ({filteredItems.length})</h2>
            <div className="public-search-wrap">
              <input
                type="text"
                className="public-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('searchItems') || 'Search by name, category, or brand...'}
              />
            </div>
            {items.length === 0 ? (
              <div className="no-items">{t('noItems') || 'No items available'}</div>
            ) : filteredItems.length === 0 ? (
              <div className="no-items">{t('noItems') || 'No items available'}</div>
            ) : (
              <>
              <h3 style={{ margin: '0 0 1rem 0', color: '#155724' }}>
                {t('inStock') || 'In Stock'} ({availableItems.length})
              </h3>
              <div className="items-grid">
                {availableItems.map(item => (
                  <div key={item.id} className="item-card">
                    <div className="item-header">
                      <h3>{item.name}</h3>
                      <div className="item-meta-badges">
                        {item.category && (
                          <span className="category-badge">{item.category}</span>
                        )}
                        {item.brand && (
                          <span className="brand-badge">{item.brand}</span>
                        )}
                      </div>
                    </div>

                    <div className="item-details">
                      <p className="description">{item.description}</p>
                    </div>
                    
                      {item.images && item.images.length > 0 && (
                        <div className="item-images">
                          <ImageGallery 
                            images={item.images} 
                            itemName={item.name}
                          />
                        </div>
                      )}

                    <div className="item-price">
                      <span className="price">
                        {formatCurrency(item.selling_price, item.currency)} {item.currency}
                      </span>
                      {item.unit_type && item.unit_type !== 'quantity' && (
                        <span className="unit-label"> / {getUnitLabel(item.unit_type)}</span>
                      )}
                    </div>

                    <div className="item-stock">
                      <span className={`stock-label ${item.is_available ? 'available' : 'unavailable'}`}>
                        {item.is_available
                          ? `${t('inStock')}: ${formatQuantity(item.quantity)} ${item.unit_type && item.unit_type !== 'quantity' ? getUnitLabel(item.unit_type) : ''}`
                          : t('outOfStock')}
                      </span>
                    </div>

                    {item.is_available && (
                      <div className="quantity-selector">
                        <button
                          className="qty-btn"
                          onClick={() => {
                            const currentQty = parseFloat(getItemQuantityValue(item.id, item.allow_fractional)) || 1;
                            const step = item.allow_fractional ? 0.1 : 1;
                            const newQty = Math.max(step, currentQty - step);
                            updateItemQuantity(item.id, item.allow_fractional ? newQty.toFixed(1) : Math.round(newQty));
                          }}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          className="qty-input-card"
                          min={item.allow_fractional ? "0.1" : "1"}
                          step={item.allow_fractional ? "0.1" : "1"}
                          value={getItemQuantityValue(item.id, item.allow_fractional)}
                          onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              addToCart(item);
                            }
                          }}
                        />
                        <button
                          className="qty-btn"
                          onClick={() => {
                            const currentQty = parseFloat(getItemQuantityValue(item.id, item.allow_fractional)) || 1;
                            const step = item.allow_fractional ? 0.1 : 1;
                            const newQty = currentQty + step;
                            updateItemQuantity(item.id, item.allow_fractional ? newQty.toFixed(1) : Math.round(newQty));
                          }}
                        >
                          +
                        </button>
                      </div>
                    )}

                    <button
                      className={`add-to-cart-btn ${!item.is_available ? 'disabled' : ''}`}
                      onClick={() => addToCart(item)}
                      disabled={!item.is_available}
                    >
                      🛒 {t('addToCart') || 'Add to Cart'}
                    </button>
                  </div>
                ))}
              </div>

              {unavailableItems.length > 0 && (
                <>
                  <h3 style={{ margin: '2rem 0 1rem 0', color: '#721c24' }}>
                    {t('outOfStock') || 'Out of Stock'} ({unavailableItems.length})
                  </h3>
                  <div className="items-grid">
                    {unavailableItems.map(item => (
                      <div key={item.id} className="item-card" style={{ opacity: 0.75 }}>
                        <div className="item-header">
                          <h3>{item.name}</h3>
                          <div className="item-meta-badges">
                            {item.category && (
                              <span className="category-badge">{item.category}</span>
                            )}
                            {item.brand && (
                              <span className="brand-badge">{item.brand}</span>
                            )}
                          </div>
                        </div>

                        <div className="item-details">
                          <p className="description">{item.description}</p>
                        </div>

                        {item.images && item.images.length > 0 && (
                          <div className="item-images">
                            <ImageGallery
                              images={item.images}
                              itemName={item.name}
                            />
                          </div>
                        )}

                        <div className="item-price">
                          <span className="price">
                            {formatCurrency(item.selling_price, item.currency)} {item.currency}
                          </span>
                          {item.unit_type && item.unit_type !== 'quantity' && (
                            <span className="unit-label"> / {getUnitLabel(item.unit_type)}</span>
                          )}
                        </div>

                        <div className="item-stock">
                          <span className="stock-label unavailable">{t('outOfStock') || 'Out of Stock'}</span>
                        </div>

                        <button className="add-to-cart-btn disabled" disabled>
                          {t('addToCart') || 'Add to Cart'}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
              </>
            )}
          </div>
        )}

        {/* Cart Sidebar */}
        {showCart && (
          <div className="cart-container">
            <h2>{t('cart')}</h2>
            {cart.length === 0 ? (
              <div className="empty-cart">{t('cartEmpty') || 'Your cart is empty'}</div>
            ) : (
              <>
                <div className="cart-items">
                  {cart.map(item => {
                    const stockItem = items.find(i => i.id === item.id);
                    const maxStock = parseFloat(stockItem?.quantity || 0);
                    const remainingStock = Math.max(0, maxStock - item.quantity);

                    return (
                    <div key={item.id} className="cart-item">
                      <div className="cart-item-info">
                        <h4>{item.name}</h4>
                        <p className="cart-item-price">
                          {formatCurrency(item.selling_price, item.currency)} {item.currency}
                          {item.unit_type && item.unit_type !== 'quantity' && (
                            <span> / {getUnitLabel(item.unit_type)}</span>
                          )}
                        </p>
                        <p className="cart-item-remaining">
                          {t('remainingStock') || 'Remaining stock'}: {remainingStock} {item.unit_type && item.unit_type !== 'quantity' ? getUnitLabel(item.unit_type) : ''}
                        </p>
                      </div>

                      <div className="cart-item-controls">
                        <button
                          className="qty-btn"
                          onClick={() => {
                            const step = item.allow_fractional ? 0.1 : 1;
                            updateQuantity(item.id, item.quantity - step);
                          }}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="0"
                          step={item.allow_fractional ? "0.1" : "1"}
                          value={item.quantity}
                          onChange={(e) => {
                            const requestedQty = parseFloat(e.target.value);
                            const stockItem = items.find(i => i.id === item.id);
                            const maxStock = parseFloat(stockItem?.quantity || 0);

                            if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
                              updateQuantity(item.id, 0);
                              return;
                            }

                            if (requestedQty > maxStock) {
                              toast.error(`${t('insufficientStock') || 'Insufficient stock'} (${maxStock})`);
                              updateQuantity(item.id, maxStock);
                              return;
                            }

                            updateQuantity(item.id, requestedQty);
                          }}
                          className="qty-input"
                        />
                        <button
                          className="qty-btn"
                          onClick={() => {
                            const step = item.allow_fractional ? 0.1 : 1;
                            updateQuantity(item.id, item.quantity + step);
                          }}
                        >
                          +
                        </button>
                      </div>

                      <div className="cart-item-total">
                        {formatCurrency(item.selling_price * item.quantity, item.currency)} {item.currency}
                      </div>

                      <button
                        className="remove-btn"
                        onClick={() => removeFromCart(item.id)}
                        title={t('remove') || 'Remove'}
                      >
                        🗑️
                      </button>
                    </div>
                  );})}
                </div>

                <div className="cart-summary">
                  {(() => {
                    const totals = calculateTotals();
                    return (
                      <>
                        {totals.hasUSD && (
                          <div className="total-row primary">
                            <strong>{t('subtotal')} USD:</strong>
                            <strong>{totals.totalUSD} USD</strong>
                          </div>
                        )}
                        {totals.hasLBP && (
                          <div className="total-row primary">
                            <strong>{t('subtotal')} LBP:</strong>
                            <strong>{totals.totalLBP} LBP</strong>
                          </div>
                        )}
                        
                        {totals.hasBothCurrencies && (
                          <div className="total-row secondary">
                            <strong>{t('exchangeRate')}:</strong>
                            <strong>1 USD = {formatCurrency(totals.exchangeRate, 'LBP')} LBP</strong>
                          </div>
                        )}
                        
                        {(totals.totalUSD > 0 || totals.totalLBP > 0) && (
                          <>
                            <div className="conversion-divider"></div>
                            <div className="total-row grand">
                              <strong>{t('grandTotal')}:</strong>
                              <strong>{totals.grandTotalUSD} USD</strong>
                            </div>
                            <div className="total-row grand">
                              <strong>{t('grandTotal')}:</strong>
                              <strong>{totals.grandTotalLBP} LBP</strong>
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>

                <button
                  className="whatsapp-order-btn"
                  onClick={sendWhatsAppOrder}
                >
                  📱 {t('orderViaWhatsApp') || 'Order via WhatsApp'}
                </button>

                <button
                  className="continue-shopping-btn"
                  onClick={() => setShowCart(false)}
                >
                  {t('continueShopping') || 'Continue Shopping'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicMarket;
